import { observable } from '@legendapp/state';
import { syncObservable } from '@legendapp/state/sync';
import { ObservablePersistLocalStorage } from '@legendapp/state/persist-plugins/local-storage';
import { supabase } from '../lib/supabase';
import { auth$ } from './auth$';
import { getTierLimit } from '@papper/shared/reedle/tiers';

// Store for list members - not synced with Legend State, just direct Supabase calls
// because this is collaborative data that needs real-time accuracy

// --- Shared List Cache ---
// Cache of list IDs that have >1 member (shared lists)
// Used for instant filtering in "hide shared articles" without async calls
export const sharedListIds$ = observable([]);

// Persist to localStorage for instant access on page load
syncObservable(sharedListIds$, {
  persist: {
    name: 'reedle-shared-list-ids',
    plugin: ObservablePersistLocalStorage,
  },
});

/**
 * Refresh the shared list cache using a single batched RPC.
 * Replaces the previous N+1 loop (one getListMembers call per list).
 * Call this on app init and when list membership changes.
 */
export const refreshSharedListsCache = async () => {
  const userId = auth$.user.get()?.id;
  if (!userId) return;

  const { data, error } = await supabase.rpc('reedle_get_shared_list_ids', {
    p_user_id: userId,
  });

  if (error) {
    console.error('[sharedListsCache] Failed to refresh:', error);
    return; // preserve existing cached value on error
  }

  const shared = (data || []).map(row => row.list_id);
  sharedListIds$.set(shared);
  console.log('[sharedListsCache] refreshed:', shared.length, 'shared lists', shared);
};

/**
 * Check if a list is shared (synchronous, from cache)
 * @param {string} listId
 * @returns {boolean}
 */
export const isListShared = (listId) => {
  const sharedIds = sharedListIds$.get() || [];
  return sharedIds.includes(listId);
};

/**
 * Find a user by email
 * @param {string} email
 * @returns {Promise<{id: string, email: string, username?: string, avatar_url?: string} | null>}
 */
export const findUserByEmail = async (email) => {
  const { data, error } = await supabase.rpc('find_user_by_email', { p_email: email });
  if (error || !data || data.length === 0) return null;
  
  const user = data[0];
  return {
    id: user.id,
    email: user.email,
    username: user.raw_user_meta_data?.username || user.raw_user_meta_data?.name || user.email.split('@')[0],
    avatar_url: user.raw_user_meta_data?.avatar_url || null,
  };
};

/**
 * Get all members of a list
 * @param {string} listId
 * @returns {Promise<Array<{id: string, user_id: string, role: string, share_progress: boolean, email: string, username: string, avatar_url: string | null}>>}
 */
export const getListMembers = async (listId) => {
  const { data, error } = await supabase.rpc('reedle_get_list_members', { p_list_id: listId });
  if (error) {
    console.error('Failed to get list members:', error);
    return [];
  }
  
  return (data || []).map(m => ({
    id: m.id,
    user_id: m.user_id,
    role: m.role,
    share_progress: m.share_progress,
    email: m.email,
    username: m.user_meta?.username || m.user_meta?.name || m.email?.split('@')[0] || 'User',
    avatar_url: m.user_meta?.avatar_url || null,
    created_at: m.created_at,
  }));
};

/**
 * Count lists where the current user is owner AND has ≥1 non-owner member.
 * Used for shared list limit enforcement.
 * @returns {Promise<number>}
 */
export const countUserSharedLists = async () => {
  const userId = auth$.user.get()?.id;
  if (!userId) return 0;

  // Query: lists where user is owner that have at least one non-owner member
  const { data, error } = await supabase
    .from('reedle_list_members')
    .select('list_id')
    .eq('user_id', userId)
    .eq('role', 'owner');

  if (error || !data?.length) return 0;

  const ownerListIds = data.map(r => r.list_id);

  // Get distinct list_ids that have non-owner members
  const { data: sharedData, error: sharedError } = await supabase
    .from('reedle_list_members')
    .select('list_id')
    .in('list_id', ownerListIds)
    .neq('user_id', userId);

  if (sharedError) return 0;
  const uniqueSharedListIds = new Set((sharedData || []).map(r => r.list_id));
  return uniqueSharedListIds.size;
};

/**
 * Add a member to a list
 * @param {string} listId
 * @param {string} userId
 * @param {string} [userTier='free'] - Current user's subscription tier
 * @returns {Promise<{success: boolean, error?: string, limitReached?: boolean}>}
 */
export const addMember = async (listId, userId, userTier = 'free') => {
  const currentUserId = auth$.user.get()?.id;

  // Check if trying to add self
  if (userId === currentUserId) {
    return { success: false, error: 'Cannot invite yourself' };
  }

  // Check if already a member
  const members = await getListMembers(listId);
  if (members.some(m => m.user_id === userId)) {
    return { success: false, error: 'Already a member' };
  }

  // Check shared list limit — only when this list has no non-owner members yet
  // (adding first non-owner member = "making it shared")
  const hasNonOwnerMembers = members.some(m => m.role !== 'owner');
  if (!hasNonOwnerMembers) {
    const limit = getTierLimit(userTier, 'sharedListsTotal');
    if (limit !== null) {
      const currentSharedCount = await countUserSharedLists();
      if (currentSharedCount >= limit) {
        return { success: false, error: `Shared list limit reached (${currentSharedCount}/${limit})`, limitReached: true };
      }
    }
  }

  const { error } = await supabase
    .from('reedle_list_members')
    .insert({ list_id: listId, user_id: userId, role: 'member' });

  if (error) {
    console.error('Failed to add member:', error);
    return { success: false, error: error.message };
  }

  // Update shared list cache - this list is now shared
  const sharedIds = sharedListIds$.get() || [];
  if (!sharedIds.includes(listId)) {
    sharedListIds$.set([...sharedIds, listId]);
  }

  return { success: true };
};

/**
 * Remove a member from a list (owner only) or leave a list (self)
 * @param {string} listId
 * @param {string} userId
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const removeMember = async (listId, userId) => {
  const { error } = await supabase
    .from('reedle_list_members')
    .delete()
    .eq('list_id', listId)
    .eq('user_id', userId);

  if (error) {
    console.error('Failed to remove member:', error);
    return { success: false, error: error.message };
  }

  // Update shared list cache - check if list is still shared
  const members = await getListMembers(listId);
  const sharedIds = sharedListIds$.get() || [];
  if (members.length <= 1 && sharedIds.includes(listId)) {
    // List is no longer shared, remove from cache
    sharedListIds$.set(sharedIds.filter(id => id !== listId));
  }

  return { success: true };
};

/**
 * Update share_progress setting for current user in a list
 * @param {string} listId
 * @param {boolean} share
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateShareProgress = async (listId, share) => {
  const userId = auth$.user.get()?.id;
  if (!userId) return { success: false, error: 'Not logged in' };
  
  const { error } = await supabase
    .from('reedle_list_members')
    .update({ share_progress: share })
    .eq('list_id', listId)
    .eq('user_id', userId);
  
  if (error) {
    console.error('Failed to update share progress:', error);
    return { success: false, error: error.message };
  }
  
  return { success: true };
};

/**
 * Add owner as member when creating a list
 * @param {string} listId
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const addOwnerAsMember = async (listId) => {
  const userId = auth$.user.get()?.id;
  if (!userId) return { success: false, error: 'Not logged in' };
  
  const { error } = await supabase
    .from('reedle_list_members')
    .insert({ list_id: listId, user_id: userId, role: 'owner' });
  
  if (error) {
    console.error('Failed to add owner as member:', error);
    return { success: false, error: error.message };
  }
  
  return { success: true };
};

/**
 * Get another user's reading progress for articles in a list
 * @param {string} userId
 * @param {string} listId
 * @returns {Promise<Object<string, {read_indices: number[], total_blocks: number}>>}
 */
export const getUserProgressForList = async (userId, listId) => {
  console.log('[getUserProgressForList] Fetching for user:', userId, 'list:', listId);

  const { data, error } = await supabase.rpc('reedle_get_user_progress_for_list', {
    p_user_id: userId,
    p_list_id: listId,
  });

  console.log('[getUserProgressForList] Response:', { data, error });

  if (error) {
    console.error('Failed to get user progress:', error);
    return {};
  }

  // Convert to map by article_id
  const progressMap = {};
  (data || []).forEach(p => {
    progressMap[p.article_id] = {
      read_indices: p.read_indices || [],
      total_blocks: p.total_blocks || 0,
    };
  });

  console.log('[getUserProgressForList] Progress map:', progressMap);
  return progressMap;
};

/**
 * Get list activities (highlights + reading activity) for a shared list
 * @param {string} listId
 * @param {number} limit - Maximum number of activities to fetch
 * @param {boolean} hideSelf - Exclude current user's activities server-side
 * @returns {Promise<Array<{activity_type: string, activity_id: string, user_id: string, username: string, article_id: string, article_title: string, article_url: string, exact_text: string|null, block_count: number|null, created_at: string}>>}
 */
export const getListActivities = async (listId, limit = 50, hideSelf = false) => {
  if (!listId || listId === 'all') return [];

  const { data, error } = await supabase.rpc('reedle_get_list_activities', {
    p_list_id: listId,
    p_limit: limit,
    p_hide_self: hideSelf,
  });

  if (error) {
    console.error('Failed to get list activities:', error);
    return [];
  }

  return data || [];
};

